import React, { useState, useEffect } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import '../FeedbackAdminPage.css';

export default function FeedbackTab() {
  const { authFetch } = useAuthFetch();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replacing, setReplacing] = useState({});

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/feedback`)
      .then(res => res.json())
      .then(data => { setItems(data); setLoading(false); })
      .catch(err => { console.error(err); setError('Failed to load feedback.'); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, newStatus) => {
    const prevItem = items.find(item => item.id === id);
    if (newStatus === 'addressed' || newStatus === 'ignored') {
      setItems(prev => prev.filter(item => item.id !== id));
    } else {
      setItems(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));
    }
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/api/feedback/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
    } catch (err) {
      console.error('Failed to update status:', err);
      setItems(prev => {
        const exists = prev.some(item => item.id === id);
        return exists
          ? prev.map(item => item.id === id ? prevItem : item)
          : [...prev, prevItem].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      });
    }
  };

  const handleStatusClick = (item, clickedStatus) => {
    const newStatus = item.status === clickedStatus ? 'pending' : clickedStatus;
    setStatus(item.id, newStatus);
  };

  const handleReplaceVideo = async (item) => {
    const { questionId, videoType } = item.metadata || {};
    if (!questionId || !videoType) return;
    setReplacing(prev => ({ ...prev, [item.id]: true }));
    try {
      await authFetch(`${process.env.REACT_APP_API_URL}/api/admin/blacklist-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId, videoType, feedbackId: item.id }),
      });
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Failed to replace video:', err);
    } finally {
      setReplacing(prev => ({ ...prev, [item.id]: false }));
    }
  };

  return (
    <div className="fa-page">
      <h2 className="fa-title">Feedback</h2>

      {loading && <p className="fa-loading">Loading…</p>}
      {error && <p className="fa-error">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <p className="fa-empty">No feedback submitted yet.</p>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="fa-table-wrapper">
          <table className="fa-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Message</th>
                <th>Page</th>
                <th>User</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className={`fa-row fa-row--${item.status}`}>
                  <td>
                    <span className={`fa-badge fa-badge--${item.type}`}>{item.type}</span>
                  </td>
                  <td className="fa-message">
                    {item.message}
                    {item.type === 'video_rating' && item.metadata?.videoUrl && (
                      <> <a href={item.metadata.videoUrl} target="_blank" rel="noreferrer" className="fa-video-link">Watch video →</a></>
                    )}
                  </td>
                  <td className="fa-page-url" title={item.page_url}>
                    {item.page_url ? item.page_url.replace(/^https?:\/\/[^/]+/, '') || '/' : '—'}
                  </td>
                  <td className="fa-user">
                    {item.azure_oid ? item.azure_oid.slice(-8) : '—'}
                  </td>
                  <td className="fa-date">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <span className={`fa-status fa-status--${item.status}`}>{item.status}</span>
                  </td>
                  <td className="fa-actions">
                    {item.type === 'video_rating' && (
                      item.status === 'addressed' ? (
                        <button className="fa-btn fa-btn--replace" disabled>Replaced ✓</button>
                      ) : (
                        <button
                          className="fa-btn fa-btn--replace"
                          onClick={() => handleReplaceVideo(item)}
                          disabled={replacing[item.id]}
                          title="Blacklist current video and fetch a replacement"
                        >
                          {replacing[item.id] ? 'Replacing…' : 'Replace Video'}
                        </button>
                      )
                    )}
                    <button
                      className={`fa-btn fa-btn--addressed${item.status === 'addressed' ? ' active' : ''}`}
                      onClick={() => handleStatusClick(item, 'addressed')}
                      title={item.status === 'addressed' ? 'Reset to pending' : 'Mark addressed'}
                    >
                      Addressed
                    </button>
                    <button
                      className={`fa-btn fa-btn--ignored${item.status === 'ignored' ? ' active' : ''}`}
                      onClick={() => handleStatusClick(item, 'ignored')}
                      title={item.status === 'ignored' ? 'Reset to pending' : 'Mark ignored'}
                    >
                      Ignored
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
